import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Icon,
  Ryecon,
  RyeconFreshnessError,
  RyeconFreshnessPassed,
  RyeconFreshnessStale,
  RyeconFreshnessUnknown,
  RyeconStatusSkipped,
  RyeconStatusWarning,
  Tooltip,
} from '@dbt-labs/sourdough';

import { FreshnessStatus, SourceAppliedStateNestedNode } from '@/__generated__/graphql';
import { truthy } from '@/util/array';
import { toFreshnessStatus } from '@/util/dbt/freshness';
import { CollapsibleSection } from '../CollapsibleSection';
import { fromSource, ResourceStatusTable } from '../ResourceStatusTable';

const freshnessToRyeconMap: Record<FreshnessStatus, Ryecon> = {
  [FreshnessStatus.Error]: RyeconFreshnessError,
  [FreshnessStatus.Warn]: RyeconStatusWarning,
  [FreshnessStatus.Unknown]: RyeconFreshnessUnknown,
  [FreshnessStatus.Pass]: RyeconFreshnessPassed,
  [FreshnessStatus.Skipped]: RyeconStatusSkipped,
  [FreshnessStatus.Outdated]: RyeconFreshnessStale,
  [FreshnessStatus.Unconfigured]: RyeconFreshnessUnknown,
};

// used to sort test results by severity
export const freshnessStatusOrder: Record<FreshnessStatus, number> = {
  [FreshnessStatus.Error]: 0,
  [FreshnessStatus.Warn]: 1,
  [FreshnessStatus.Outdated]: 2,
  [FreshnessStatus.Unconfigured]: 2,
  [FreshnessStatus.Unknown]: 3,
  [FreshnessStatus.Pass]: 4,
  [FreshnessStatus.Skipped]: 5,
};

const freshnessStatusMessages: Record<FreshnessStatus, string> = {
  [FreshnessStatus.Error]: 'One or more upstream sources failed its freshness checks',
  [FreshnessStatus.Warn]: 'One or more upstream sources are warning',
  [FreshnessStatus.Outdated]:
    "Freshness hasn't run recently for one or more upstream sources",
  [FreshnessStatus.Unconfigured]:
    'One or more upstream sources do not have freshness checks configured',
  [FreshnessStatus.Unknown]:
    'Freshness status of one or more upstream sources is unknown',
  [FreshnessStatus.Pass]: 'All upstream sources are fresh',
  [FreshnessStatus.Skipped]:
    'Freshness checks were skipped for one or more upstream sources',
};

interface UpstreamSourcesSectionProps {
  sources: SourceAppliedStateNestedNode[] | undefined;
}

export const UpstreamSourcesSection: React.FC<UpstreamSourcesSectionProps> = ({
  sources,
}) => {
  const { state } = useLocation();
  const isSourcesOpen = state?.isSourcesOpen;

  const { upstreamSources, worstFreshnessStatus, tooltipMessage } = useMemo(() => {
    if (!sources) {
      return {
        upstreamSources: undefined,
        worstFreshnessStatus: FreshnessStatus.Unknown,
        tooltipMessage:
          'The freshness status of upstream sources could not be determined',
      };
    }

    const upstreamSources = sources?.filter(truthy).map((source) => fromSource(source));

    let worstStatus = FreshnessStatus.Skipped;
    upstreamSources.forEach((source) => {
      const status = toFreshnessStatus(source.status);
      if (freshnessStatusOrder[status] < freshnessStatusOrder[worstStatus]) {
        worstStatus = status;
      }
    });

    const message =
      upstreamSources.length === 0
        ? 'This model has no upstream sources'
        : freshnessStatusMessages[worstStatus];

    // sort by freshness status severity, then name alphabetically if tie
    upstreamSources.sort((a, b) => {
      const statusComparison =
        freshnessStatusOrder[toFreshnessStatus(a.status)] -
        freshnessStatusOrder[toFreshnessStatus(b.status)];
      if (statusComparison !== 0) {
        return statusComparison;
      }
      return a.name.localeCompare(b.name);
    });

    return {
      upstreamSources,
      worstFreshnessStatus: worstStatus,
      tooltipMessage: message,
    };
  }, [sources]);

  return (
    <>
      {upstreamSources && upstreamSources.length > 0 && (
        <CollapsibleSection
          isOpen={isSourcesOpen}
          closeAltText={`Hide upstream source details`}
          expandAltText={`Show upstream source details`}
          toExpand={<ResourceStatusTable resources={upstreamSources} />}
          disable={upstreamSources.length === 0}
          shouldIndent={upstreamSources.length > 0}
          className="-mb-[1px] mt-1 flex w-full overflow-hidden border-b border-borderMuted p-4 text-fgMain"
        >
          <span
            className="flex"
            data-testid={`freshness-status-${worstFreshnessStatus}`}
          >
            <span className="flex items-center">
              <Tooltip
                content={tooltipMessage}
                className="pointer-events-auto flex items-center"
              >
                <Icon
                  size="md"
                  ryecon={freshnessToRyeconMap[worstFreshnessStatus]}
                  className="pointer-events-auto align-middle"
                />
                <div className="sr-only">{tooltipMessage}</div>
              </Tooltip>
            </span>
            <span>
              <p className="font-label-lg mx-2 align-middle">Upstream sources</p>
            </span>
          </span>
        </CollapsibleSection>
      )}
    </>
  );
};
